/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import CustomModal from "@ui-components/Modal/CustomModal";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";

import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusV3.svg";
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../shared/assets/Icons/Icon_Delete.svg";
import { ReactComponent as IconEye } from "../../../../shared/assets/Icons/Icon_Eye.svg";
import { ThirdParty, columns, CreateThirdPartyPortPayload } from "./ThirdPartyData";
import { getAllThirdPartyPortsAdmin, getThirdPartyPortDetails, createThirdPartyPort, updateThirdPartyPort, deleteThirdPartyPort } from "./request/request";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { useAppSelector } from "@redux/hooks";
import PartnerCustomerDropdown from "@shared/navigation/SideNav/components/PartnerCustomerDropdown";
import { useAuth0 } from "@auth0/auth0-react";
import { format } from "date-fns";

const defaultThirdPartyForm: ThirdParty = {
    id: "",
    clientName: "",
    projectId: "",
    linkType: "",
    purchaseOrder: "",
    projectName: "",
    origin: "",
    destination: "",
    capacity: "",
    vendorServiceReference: "",
    contractStartDate: "",
    contractEndDate: "",
    contractStatus: "",
    carrier: "",
    tenantId: "",
};

const defaultCreateThirdPartyPortForm: CreateThirdPartyPortPayload = {
    tenantId: "",
    providerId: "",
    vendorServiceReference: "",
    linkId: "",
    projectId: "",
    linkType: "",
    purchaseOrder: "",
    projectName: "",
    origin: "",
    destination: "",
    capacity: "",
    contractStartDate: "",
    contractEndDate: "",
    contractStatus: "",
    carrier: "",
    localLoop: "",
    lastStateUpdate: "",
};

interface DateTimeRange {
    start: Date | null;
    end: Date | null;
}

const providerLabelToValue: Record<string, string> = {
    "Console Connect": "consoleConnect",
    "Equinix": "equinix",
    "Megaport": "megaport"
};

export default function ThirdPartyInventoryPage() {
    const api = useAxiosAuth0();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [providerDetails, setProviderDetails] = useState<Record<string, any> | null>(null);
    const [isProviderLoading, setIsProviderLoading] = useState(false);
    const { isLoading: authLoading, isAuthenticated, getAccessTokenSilently } =
    useAuth0();
  
    // States
    const [thirdPartyList, setThirdPartyList] = useState<ThirdParty[]>([]);
    const [thirdPartyForm, setThirdPartyForm] = useState<ThirdParty>(defaultThirdPartyForm);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [thirdPartyToDelete, setThirdPartyToDelete] = useState<ThirdParty | null>(null);
    const [isNoFormatModalOpen, setIsNoFormatModalOpen] = useState(false);
    const [createThirdPartyForm, setCreateThirdPartyForm] = useState<CreateThirdPartyPortPayload>(defaultCreateThirdPartyPortForm);
    const [startDate, setStartDate] = useState<DateTimeRange>({ start: null, end: null });
    const [endDate, setEndDate] = useState<DateTimeRange>({ start: null, end: null });
    const { tenantId, tenantName } = useAppSelector(state => state.selection);
    const [modalKey, setModalKey] = useState(0);
    const [successAlert, setSuccessAlert] = useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = useState("");

    const tenantType = useAppSelector((s) => s.userProfile.TenantType);

    // Update tenantId in form when selection changes
    useEffect(() => {
        setCreateThirdPartyForm(prev => ({
            ...prev,
            tenantId: tenantId.toString()
        }));
    }, [tenantId]);

    // 1) Fetch real data on mount
    useEffect(() => {
        const fetchThirdParties = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await getAllThirdPartyPortsAdmin(api);
                const list: ThirdParty[] = response.data?.data ?? response.data;
                setThirdPartyList(list);
            } catch (err) {
                console.error("Failed to load 3rd-party ports:", err);
                setError(err instanceof Error ? err.message : "Unknown error");
            } finally {
                setIsLoading(false);
            }
        };

        fetchThirdParties();
    }, [api]);

    // Handlers
    const handleAddThirdParty = () => {
        setCreateThirdPartyForm(defaultCreateThirdPartyPortForm);
        setEditMode(false);
        setStartDate({ start: null, end: null });
        setEndDate({ start: null, end: null });
        setModalKey(prev => prev + 1);
        setIsModalOpen(true);
    };

    const handleViewThirdParty = async (item: ThirdParty) => {
        setIsProviderLoading(true);
        setThirdPartyForm(item);
        setEditMode(false);
        setIsNoFormatModalOpen(true);
        setProviderDetails(null);

        try {
            const response = await getThirdPartyPortDetails(api, item.id, item.tenantId);
            const providerData = response.data?.data.provider ?? response.data.provider;
            setProviderDetails(providerData || {});
        } catch (err) {
            console.error("Failed to load provider details:", err);
            setError(err instanceof Error ? err.message : "Unknown error");
            setProviderDetails({});
        } finally {
            setIsProviderLoading(false);
        }
    };

    const handleEditThirdParty = (item: ThirdParty) => {
        setThirdPartyForm(item);
        setCreateThirdPartyForm({ ...defaultCreateThirdPartyPortForm, ...item });
        setStartDate({ start: item.contractStartDate ? new Date(item.contractStartDate) : null, end: null });
        setEndDate({ start: item.contractEndDate ? new Date(item.contractEndDate) : null, end: null });
        setEditMode(true);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (item: ThirdParty) => {
        setThirdPartyToDelete(item);
        setIsAlertOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (thirdPartyToDelete) {
            try {
                setIsLoading(true);
                await deleteThirdPartyPort(api, thirdPartyToDelete.id);
                setSuccessAlertMessage("Third party deleted successfully!");
                setSuccessAlert(true);
                // Refresh the list after deletion
                const response = await getAllThirdPartyPortsAdmin(api);
                const list: ThirdParty[] = response.data?.data ?? response.data;
                setThirdPartyList(list);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Unknown error");
            } finally {
                setIsLoading(false);
                setIsAlertOpen(false);
                setThirdPartyToDelete(null);
            }
        }
    };

    const handleFieldChange = (
        fieldKey: keyof ThirdParty,
        newValue: string
    ) => {
        setThirdPartyForm(prev => ({
            ...prev,
            [fieldKey]: newValue
        }));
    };

    const handleCreateThirdPartyFieldChange = (
        fieldKey: keyof CreateThirdPartyPortPayload,
        newValue: string
    ) => {
        setCreateThirdPartyForm(prev => ({
            ...prev,
            [fieldKey]: newValue
        }));
    };

    const handleSaveThirdParty = async () => {
        try {
            setIsLoading(true);
            const formData = {
                ...createThirdPartyForm,
                providerId: providerLabelToValue[createThirdPartyForm.providerId] || createThirdPartyForm.providerId,
                contractStartDate: startDate.start ? format(startDate.start, "yyyy-MM-dd") : "",
                contractEndDate: endDate.start ? format(endDate.start, "yyyy-MM-dd") : "",
                tenantId: tenantId.toString(),
                clientName: tenantName
            };

            if (editMode) {
                await updateThirdPartyPort(api, thirdPartyForm.id, thirdPartyForm.tenantId, formData);
                setSuccessAlertMessage("Third party updated successfully!");
            } else {
                await createThirdPartyPort(api, formData);
                setSuccessAlertMessage("Third party created successfully!");
            }
            setSuccessAlert(true);

            // Refresh list
            const response = await getAllThirdPartyPortsAdmin(api);
            const list: ThirdParty[] = response.data?.data ?? response.data;
            setThirdPartyList(list);
            setIsModalOpen(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Unknown error");
        } finally {
            setIsLoading(false);
        }
    };

    // Enhanced columns with actions
    const enhancedColumns: GenericTableColumn<ThirdParty>[] = [
        ...columns.slice(0, -1),
        {
            header: "Actions",
            accessor: "id" as keyof ThirdParty,
            fixed: 'right',
            width: '100px',
            renderCell: (item: ThirdParty) => (
                <div className="flex items-center gap-2">
                    <GenericButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleViewThirdParty(item)}
                        icon={<IconEye className="w-6 h-6" />}
                    >
                        View
                    </GenericButton>
                    <GenericButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleEditThirdParty(item)}
                        icon={<IconEdit className="w-10 h-10" />}
                    >
                        Edit
                    </GenericButton>
                    <GenericButton
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteClick(item)}
                        icon={<IconDelete className="w-6 h-6" />}
                    >
                        Delete
                    </GenericButton>
                </div>
            ),
        },
    ];

      // --------------------------------------------------
      // 4) Handlers de selección
      // --------------------------------------------------
        //User selects a partner from the dropdown
        const handleSelectPartner = async (nameDisplay: string) => {
        //   const found = partnersData.find((p) => p.nameDisplay === nameDisplay);
        //   if (!found) return;
        //   dispatch(setPartner({ id: found.id, name: found.nameDisplay }));
        //   // loading customers for the new partner will reset tenant in Redux
        //   await loadCustomers(found.id);
        };

    return (
        <div className="relative">
            <div className="mb-4 p-4 bg-[var(--semantic-bg-primary)] border border-semantic-border-secondary rounded-sm">
                <div className="flex items-end justify-end">
                    <GenericButton
                        label={
                            <div className="flex items-center gap-2">
                                <IconPlus className="w-4 h-4" />
                                <span>Add third party</span>
                            </div>
                        }
                        variant="brand"
                        size="md"
                        onClick={handleAddThirdParty}
                    />
                </div>
            </div>

            <GenericTable
                columns={enhancedColumns}
                data={thirdPartyList}
                width="100%"
                initialRowsPerPage={10}
                rowsPerPageOptions={[5, 10, 20]}
                tableClassName="bg-semantic-bg-primary"
                maxTableBodyHeight="calc(100vh - 300px)"
            />

            {/* Add/Edit Modal */}
            <GenericModal
                key={modalKey}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editMode ? "Edit Third Party" : "Add Third Party"}
                size="lg"
            >
                <div className="grid grid-cols-3 gap-4 mb-4">
                
                    

                    {/* Provider ID Dropdown */}
                    <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                            Provider
                        </label>
                        <GenericDropdown
                            items={["Console Connect", "Equinix", "Megaport"]}
                            defaultLabel="Select Provider"
                            value={createThirdPartyForm.providerId}
                            onSelect={(value) => handleCreateThirdPartyFieldChange("providerId", value)}
                            minWidthClass="min-w-[180px]"
                            maxWidthClass="max-w-[240px]"
                        />
                    </div>

                    {/* Link Type Dropdown */}
                    <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                            Link Type
                        </label>
                        <GenericDropdown
                            items={["Edge Port", "Port", "Local Loop"]}
                            defaultLabel="Select Link Type"
                            value={createThirdPartyForm.linkType}
                            onSelect={(value) => handleCreateThirdPartyFieldChange("linkType", value)}
                            minWidthClass="min-w-[180px]"
                            maxWidthClass="max-w-[240px]"
                        />
                    </div>

                    {/* Status Dropdown */}
                    <div className="flex flex-col">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                            Status
                        </label>
                        <GenericDropdown
                            items={["ACTIVE","PENDING", "CONFIGURED", "PROVISIONING",  "INACTIVE", "DEPROVISIONING", "FAILED"]}
                            defaultLabel="Select Status"
                            value={createThirdPartyForm.contractStatus}
                            onSelect={(value) => handleCreateThirdPartyFieldChange("contractStatus", value)}
                            minWidthClass="min-w-[180px]"
                            maxWidthClass="max-w-[240px]"
                        />
                    </div>
                    {/* Partner/Customer Selection */}
                    <div className="flex flex-col gap-2">
                        <label className="mb-1 text-sm font-medium text-semantic-text-secondary">Tenant</label>
                        <PartnerCustomerDropdown
                            tenantType={tenantType}
                            isLoading={authLoading}
                            isAuthenticated={isAuthenticated}
                            getAccessTokenSilently={getAccessTokenSilently}
                        />
                    </div>

                    {/* Contract Start Date */}
                    <div className="flex flex-col">
                        <GenericDateTimeRangePicker
                            label="Contract Start Date"
                            value={startDate}
                            onChange={setStartDate}
                            displayFormat="dd/MM/yyyy"
                            singleDate={true}
                        />
                    </div>

                    {/* Contract End Date */}
                    <div className="flex flex-col">
                        <GenericDateTimeRangePicker
                            label="Contract End Date"
                            value={endDate}
                            onChange={setEndDate}
                            displayFormat="dd/MM/yyyy"
                            singleDate={true}
                        />
                    </div>
                    {/* Other fields */}
                    {Object.keys(defaultCreateThirdPartyPortForm)
                        .filter(key => 
                            key !== 'providerId' && 
                            key !== 'linkType' && 
                            key !== 'clientName' && 
                            key !== 'contractStartDate' && 
                            key !== 'contractEndDate' &&
                            key !== 'contractStatus' &&
                            key !== 'tenantId' // tenantId handled by dropdown
                        )
                        .map((key) => {
                            let placeholder = '';
                            switch (key) {
                                case 'vendorServiceReference':
                                    placeholder = 'e.g. SR245610';
                                    break;
                                case 'linkId':
                                    placeholder = 'e.g. link-123';
                                    break;
                                case 'projectId':
                                    placeholder = 'e.g. proj-001';
                                    break;
                                case 'purchaseOrder':
                                    placeholder = 'e.g. PO-2024-001';
                                    break;
                                case 'projectName':
                                    placeholder = 'e.g. Migration Project';
                                    break;
                                case 'origin':
                                    placeholder = 'e.g. NYC1';
                                    break;
                                case 'destination':
                                    placeholder = 'e.g. LON1';
                                    break;
                                case 'capacity':
                                    placeholder = 'e.g. 10Gbps';
                                    break;
                                case 'carrier':
                                    placeholder = 'e.g. CarrierX';
                                    break;
                                case 'localLoop':
                                    placeholder = 'e.g. Loop-42';
                                    break;
                                case 'lastStateUpdate':
                                    placeholder = 'e.g. 2024-06-10 12:00';
                                    break;
                                default:
                                    placeholder = '';
                            }
                            return (
                                <GenericInput
                                    key={key}
                                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                                    value={createThirdPartyForm[key as keyof CreateThirdPartyPortPayload] ?? ""}
                                    onChange={(value: string) => 
                                        handleCreateThirdPartyFieldChange(key as keyof CreateThirdPartyPortPayload, value)
                                    }
                                    placeholder={placeholder}
                                />
                            );
                        })}


                </div>
                <div className="flex justify-end space-x-2">
                    <button
                        className="
                            px-4 py-2
                            text-sm
                            font-medium
                            text-semantic-text-secondary
                            bg-semantic-bg-primary
                            border border-semantic-border-secondary
                            rounded
                            hover:bg-semantic-state-active
                            transition-colors
                        "
                        onClick={() => setIsModalOpen(false)}
                    >
                        Cancel
                    </button>
                    <button
                        className="
                            px-4 py-2
                            text-sm
                            font-medium
                            text-white
                            bg-semantic-text-brand
                            rounded
                            hover:bg-opacity-90
                            transition-colors
                        "
                        onClick={handleSaveThirdParty}
                    >
                        Save
                    </button>
                </div>
            </GenericModal>

            {/* CustomModal for provider details */}
            <CustomModal
                isOpen={isNoFormatModalOpen}
                onClose={() => setIsNoFormatModalOpen(false)}
                data={providerDetails || {}}
                title="Provider Details"
                isLoading={isProviderLoading}
            />

            {/* Delete Alert */}
            <GenericAlert
                isOpen={isAlertOpen}
                onClose={() => setIsAlertOpen(false)}
                title="Are you sure?"
                description={`This will permanently delete the third party ${thirdPartyToDelete?.id} from the inventory.`}
                cancelLabel="Cancel"
                confirmLabel="Delete"
                onConfirm={handleConfirmDelete}
            />

            {/* Success Alert */}
            <GenericAlert
                isOpen={successAlert}
                onClose={() => setSuccessAlert(false)}
                onConfirm={() => setSuccessAlert(false)}
                title="Success"
                description={successAlertMessage}
                confirmLabel="OK"
                variant="success"
            />
        </div>
    );
}
